"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Search } from "lucide-react";

import { countries } from "@/data/countries";

interface Props {
    value: string;
    onChange: (code: string) => void;
    variant?: "light" | "dark";
}

export const CountryCodeSelect = ({ value, onChange, variant = "light" }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    const isDark = variant === "dark";

    const filteredCountries = countries.filter(c => 
        c.name.toLowerCase().includes(search.toLowerCase()) || 
        c.code.includes(search)
    );

    const selectedCountry = countries.find(c => c.code === value) || countries[0];

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative isolate" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-3 py-3 transition-colors group h-full ${
                    isDark 
                    ? "bg-transparent text-white border-white/5 hover:bg-white/5" 
                    : "bg-slate-50 text-black border-black/10 hover:bg-slate-100 border-r"
                }`}
            >
                <span className="text-xl">{selectedCountry.flag}</span>
                <span className={`font-mono text-xs font-medium ${isDark ? 'text-white/60' : 'text-black/80'}`}>{selectedCountry.code}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isDark ? 'text-white/20' : 'text-black/40'} group-hover:${isDark ? 'text-white/40' : 'text-black/60'} ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.95 }}
                        className={`absolute left-0 top-full mt-2 w-64 shadow-2xl rounded-xl z-[9999] overflow-hidden border ${
                            isDark 
                            ? "bg-zinc-950 border-white/10 text-white" 
                            : "bg-white border-black/5 text-black"
                        }`}
                    >
                        <div className={`p-3 border-b flex items-center gap-2 ${
                            isDark ? "border-white/10 bg-zinc-900" : "border-black/10 bg-slate-100"
                        }`}>
                            <Search className={`w-3 h-3 ${isDark ? 'text-white/40' : 'text-black/40'}`} />
                            <input 
                                type="text"
                                placeholder="Search country..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full bg-transparent border-none focus:ring-0 text-xs font-sans placeholder:text-white/20 text-current"
                                autoFocus
                            />
                        </div>
                        <div className="max-h-60 overflow-y-auto py-2 custom-scrollbar">
                            {filteredCountries.map((c) => (
                                <button
                                    key={c.name}
                                    type="button"
                                    onClick={() => {
                                        onChange(c.code);
                                        setIsOpen(false);
                                        setSearch("");
                                    }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left ${
                                        isDark ? "hover:bg-white/5" : "hover:bg-slate-50"
                                    }`}
                                >
                                    <span className="text-lg">{c.flag}</span>
                                    <div className="flex flex-col">
                                        <span className={`text-[10px] uppercase tracking-widest font-mono font-bold ${isDark ? 'text-white/40' : 'text-black/60'}`}>{c.name}</span>
                                        <span className={`text-sm font-medium ${isDark ? 'text-white/90' : 'text-black/90'}`}>{c.code}</span>
                                    </div>
                                </button>
                            ))}
                            {filteredCountries.length === 0 && (
                                <div className={`p-4 text-center text-[10px] uppercase tracking-widest ${isDark ? 'text-white/20' : 'text-black/20'}`}>No results</div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
